import React from 'react';
import {
  arrayOf, bool, func, number, shape, string, objectOf
} from 'prop-types';
import { Typography, TextField, InputAdornment, IconButton } from '@one-thd/sui-atomic-components';
import { Close } from '@one-thd/sui-icons';

const MIN_AREA_NUMBER = 1;

export const CalculatorInputElement = ({
  inputType,
  errorBox,
  index,
  valueHeights,
  valueWidths,
  valueDepths,
  valueDescription,
  valueAreas,
  showDescriptions,
  lineItemName,
  calculatorType,
  dimensionNames,
  step,
  handleInputChange,
  removeInputElement,
  hideAreaText
}) => {
  const inputClasses = 'sui-mr-3';

  const dimensions = (dimensionNames && dimensionNames.length !== 0)
    ? dimensionNames.reduce((names, currentDimension, i) => {
      const dimensionList = names;
      dimensionList['dimension' + (i + 1)] = currentDimension;
      return dimensionList;
    }, {})
    : { dimension1: 'Length', dimension2: 'Width' };

  return (
    <div key={index} className="sui-my-2">
      <div className="sui-flex sui-items-center">
        {!hideAreaText && (
          <div className="sui-mr-4">
            <Typography weight="bold">{`${lineItemName} ${index + 1}`}</Typography>
          </div>
        )}
        {step > MIN_AREA_NUMBER
          && (
            <IconButton icon={Close} color="brand" onClick={() => removeInputElement(index, inputType)} />
          )}
      </div>
      <div className="sui-flex sui-flex-col sm:sui-flex-row sui-mt-1">
        {showDescriptions && (
          <div className={inputClasses}>
            <TextField
              fullWidth
              label="Description:"
              name="description"
              onChange={(event) => handleInputChange(event, index, false, inputType)}
              value={valueDescription[index]}
            />
          </div>
        )}
        {inputType === 'widthHeight' && (
          <>
            <div className={inputClasses}>
              <TextField
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ft</InputAdornment>
                  )
                }}
                label={`${dimensions.dimension1}:`}
                onChange={(event) => handleInputChange(event, index, true, inputType)}
                type="number"
                value={valueHeights[index]}
                status={errorBox.heightError ? 'error' : null}
              />
            </div>
            <div className={inputClasses}>
              <TextField
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">ft</InputAdornment>
                  )
                }}
                label={`${dimensions.dimension2}:`}
                onChange={(event) => handleInputChange(event, index, false, inputType)}
                type="number"
                value={valueWidths[index]}
                status={errorBox.widthError ? 'error' : null}
              />
            </div>
          </>
        )}
        {inputType === 'area' && (
          <div data-testid="calculator-input" className={inputClasses}>
            <TextField
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">sq. ft</InputAdornment>
                )
              }}
              label="Square Footage"
              onChange={(event) => handleInputChange(event, index, false, inputType)}
              type="number"
              value={valueAreas[index].value}
              status={errorBox.areaError ? 'error' : null}
            />
          </div>
        )}
        {calculatorType === 'Volume' && (
          <div className={inputClasses}>
            <TextField
              fullWidth
              helpMessage="3 in. recommended"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">in</InputAdornment>
                )
              }}
              label="Depth:"
              name="depth"
              onChange={(event) => handleInputChange(event, index, false, inputType)}
              type="number"
              value={valueDepths[index]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

CalculatorInputElement.propTypes = {
  inputType: string.isRequired,
  index: number.isRequired,
  errorBox: shape({
    widthError: bool,
    heightError: bool,
    areaError: bool
  }).isRequired,
  valueHeights: arrayOf(string).isRequired,
  valueWidths: arrayOf(string).isRequired,
  valueDepths: arrayOf(string),
  step: number.isRequired,
  lineItemName: string,
  calculatorType: string,
  dimensionNames: arrayOf(string),
  handleInputChange: func.isRequired,
  removeInputElement: func.isRequired,
  valueDescription: arrayOf(string),
  showDescriptions: bool,
  hideAreaText: bool,
  valueAreas: arrayOf(shape({
    value: string,
    changed: bool,
  }).isRequired).isRequired,
};

CalculatorInputElement.defaultProps = {
  lineItemName: '',
  calculatorType: 'Area',
  dimensionNames: ['Width', 'Height'],
  valueDepths: ['3'],
  valueDescription: [''],
  showDescriptions: false,
  hideAreaText: false
};
